import { saveAs } from "file-saver";
import Zip from "jszip";
import { DateTime, Interval } from "luxon";
import { utils, write } from "xlsx";
import initSqlDb from "@/utils/duckdb-client";
import OutputNodalPCToolbar from "@/components/OutputNodalPCToolbar.vue";
import NPCOutput from "@/components/dashboards/NPC.vue";
import { camelToTitleCase, snakeToCamelCase, snakeToTitleCase } from "@/plugins/utils";
import { sqlToObject } from "../sql-utils";

export default {
  data() {
    return {
      npcDb: null,
      npcDowloadProgress: { value: 0, text: "" },
      npcOutput: null,
      npcTimeseriesDialog: false,
    };
  },
  computed: {
    nodalPCOutput() {
      if (this.npcOutput) {
        const NodalPCDashboard = {
          component: NPCOutput,
          props: {
            selectedTab: this.NodalPCToolbarSettings.tab,
            viewMode: this.NodalPCToolbarSettings.viewMode,
            caseOutputs: this.npcOutput,
            inputCase: this.currentCase,
            logoUrl: this.logoUrl,
            npcTimeseriesDialog: this.npcTimeseriesDialog,
          },
          cardProps: {
            flat: true,
            outlined: false,
          },
        };

        const page = {
          title: "Nodal Production Cost",
          component: OutputNodalPCToolbar,
          props: { settings: this.NodalPCToolbarSettings },
          sections: [NodalPCDashboard],
        };
        return page;
      }
      return {
        title: "Nodal Production Cost",
        sections: [],
      };
    },
  },
  methods: {
    async parseNPCSqlYearly(db) {
      const { timeline_settings } = this.currentCase.settings;
      const nYears = Math.ceil(
        Interval.fromDateTimes(
          DateTime.fromISO(timeline_settings.start_date),
          DateTime.fromISO(timeline_settings.end_date)
        ).length("years")
      );
      let years = Array(nYears)
        .fill(0)
        .map((_v, i) => DateTime.fromISO(timeline_settings.start_date).year + i);

      const yearsResponse = await db.exec(`SELECT DISTINCT year from yearly_system`);
      years = yearsResponse[0] ? yearsResponse[0].values.map((row) => row[0]) : [];
      const yearEntries = years.map(async (year) => {
        const tableNames = [
          "demands",
          "hydro_generators",
          "markets",
          "renewable_generators",
          "storages",
          "thermal_generators",
          "nodes",
          "system",
        ];
        const obj = tableNames.reduce(
          (acc1, tableName) => ({ ...acc1, [snakeToCamelCase(tableName)]: [] }),
          {}
        );
        await Promise.all(
          tableNames.map(async (resourceType) => {
            const response = await db.exec(
              `SELECT * FROM 'yearly_${resourceType}' where year = ${year}`
            );
            if (response[0]) {
              const objectKeys = response[0].columns;
              const jsonArray = response[0].values.map((row) => sqlToObject(objectKeys, row));
              obj[snakeToCamelCase(resourceType)].push(...jsonArray);
            }
          })
        );
        return [year, obj];
      }, {});
      return Object.fromEntries(await Promise.all(yearEntries));
    },
    async parseNPCSqlNodalDemand(db) {
      const response = await db.exec(
        `SELECT id, datetime, demand, renewableGeneration FROM 'nodes'`
      );
      if (response[0]) {
        // Title-cased datetimes are interpreted as unix timestamps.
        const objectKeys = response[0].columns.map((colName) =>
          colName.replace("datetime", "Datetime")
        );
        const jsonArray = response[0].values.map((row) => sqlToObject(objectKeys, row));
        return jsonArray;
      }
      return [];
    },
    async parseNPCSqlOutputs() {
      const output = this.outputs.find((v) => v.type_name === "PRODUCTION_COST");

      try {
        this.npcDb = await initSqlDb(output);

        this.npcOutput = Object.freeze({
          yearlyResults: await this.parseNPCSqlYearly(this.npcDb),
          nodalDemands: await this.parseNPCSqlNodalDemand(this.npcDb),
        });
        // Keep connection open for other queries
      } catch (error) {
        console.error("Error parsing NPC outputs:", error);
        if (this.npcDb) {
          await this.npcDb.close();
          this.npcDb = null;
        }
      }
    },
    getUnit(key) {
      if (["Lost Load", "Demand", "Served Demand", "Energy Demand", "Generation"].includes(key)) {
        return " (MWh)";
      }
      if (key.includes("Cost")) {
        return " ($)";
      }
      if (
        [
          "Avg Lost Load",
          "Avg Served Demand",
          "Avg Demand",
          "Peak Load",
          "Avg Generation",
        ].includes(key)
      ) {
        return " (MW)";
      }
      if (["Sales", "P L"].includes(key)) {
        return " ($)";
      }
      if (["Avg PL", "Avg Purchases"].includes(key)) {
        return " ($/h)";
      }
      if (key === "Avg Srmc") {
        return " ($/MWh)";
      }
      if (key === "Average Price") {
        return " ($/MWh)";
      }
      if (["Emission", "Total Emissions"].includes(key)) {
        return " (tons)";
      }
      return "";
    },
    annualSummaryFromDB(db) {
      const workbook = utils.book_new();
      [
        "demands",
        "hydro_generators",
        "markets",
        "renewable_generators",
        "storages",
        "thermal_generators",
        "nodes",
        "system",
      ].forEach((resourceType) => {
        const response = db.exec(`SELECT * FROM 'yearly_${resourceType}'`);
        if (response[0]) {
          const objectKeys = response[0].columns.map(
            (colName) => `${camelToTitleCase(colName)}${this.getUnit(camelToTitleCase(colName))}`
          );
          const jsonArray = response[0].values.map((row) => sqlToObject(objectKeys, row));
          if (jsonArray.length) {
            const opts = {
              header: Object.keys(jsonArray[0]),
            };
            const worksheet = utils.json_to_sheet(jsonArray, opts);
            utils.book_append_sheet(workbook, worksheet, snakeToTitleCase(resourceType));
          }
        }
      });
      return { workbook, name: "Annual Summary.xlsx" };
    },
    yearlyMetricByFuelTypeFromInput(
      metric,
      years,
      { avg = false, onlyThermal = false, unit = "MW" } = {}
    ) {
      const { settings } = this.currentCase;
      const { year_start: modelStartYear } = settings;
      const yearIndexes = years.map((year) => year - modelStartYear);
      const sources = [
        ...new Set(
          this.currentCase.input.generators
            .filter((v) => (onlyThermal ? v.type === "THERMAL" : true))
            .map((v) => v.source)
        ),
      ];
      const sourcesRes = sources.map((source) => {
        const sourcesGenerators = this.currentCase.input.generators.filter(
          (v) => v.source === source
        );
        const generatorsMetric = sourcesGenerators.map((v) =>
          yearIndexes.map((index) => v[metric][index])
        );
        return [
          `${source} (${unit})`,
          ...generatorsMetric
            .reduce(
              (acc, array) => acc.map((sum, i) => sum + array[i] || 0),
              new Array(yearIndexes.length).fill(0)
            )
            .map((v) => (avg ? v / generatorsMetric.length : v)),
        ];
      });
      return sourcesRes;
    },
    yearlyStorageMetricFromInput(metric, years) {
      const { settings, input } = this.currentCase;
      const { year_start: modelStartYear } = settings;
      const yearIndexes = years.map((year) => year - modelStartYear);
      const { storage } = input;
      const metricValues = storage.map((v) => yearIndexes.map((index) => v[metric][index]));

      return metricValues.reduce(
        (acc, array) => acc.map((sum, i) => sum + array[i] || 0),
        new Array(yearIndexes.length).fill(0)
      );
    },
    yearlyMetricByNodeFromInput(
      metric,
      years,
      { unit = "MW", includeStorage = false, storageMetric = "" } = {}
    ) {
      const { settings, input } = this.currentCase;
      const { year_start: modelStartYear } = settings;
      const { nodes: inputNodes, generators, storage } = input;

      const yearIndexes = years.map((year) => year - modelStartYear);
      const nodes = inputNodes.map((node) => {
        const nodeGenerators = generators.filter((g) => g.bus_id === node.id);
        const nodeStorages = includeStorage ? storage.filter((s) => s.bus_id === node.id) : [];
        return [
          `${node.name} (${unit})`,
          ...yearIndexes.map(
            (index) =>
              nodeGenerators.reduce((acc, generator) => acc + generator[metric][index], 0) +
              nodeStorages.reduce((acc, v) => acc + v[storageMetric][index], 0)
          ),
        ];
      });
      return nodes;
    },
    metricBySource(db, colName, { avg = false, unit = "MW", valueModifier = (v) => v } = {}) {
      const sources = [...new Set(this.currentCase.input.generators.map((v) => v.source))];
      const yearsResponse = db.exec(`SELECT DISTINCT year from yearly_system`);
      const years = yearsResponse[0] ? yearsResponse[0].values.map((row) => row[0]) : [];
      const yearsPivotSql = years
        .map((year) => `SUM(case when t2.year=${year} then t2.${colName} end) '${year}'`)
        .join();
      const thermalResponse = db.exec(
        `SELECT t1.id, ${yearsPivotSql}
        FROM yearly_thermal_generators t1
        join yearly_thermal_generators t2 on t1.id = t2.id and t1.year = t2.year
        group by t1.id`
      );
      const renewableResponse = db.exec(
        `SELECT t1.id, ${yearsPivotSql}
        FROM yearly_renewable_generators t1
        join yearly_renewable_generators t2 on t1.id = t2.id and t1.year = t2.year
        group by t1.id`
      );
      const hydroResponse = db.exec(
        `SELECT t1.id, ${yearsPivotSql}
        FROM yearly_hydro_generators t1
        join yearly_hydro_generators t2 on t1.id = t2.id and t1.year = t2.year
        group by t1.id`
      );
      const generation = [
        ...(thermalResponse[0]
          ? thermalResponse[0].values.map((row) => sqlToObject(["id", ...years], row))
          : []),
        ...(renewableResponse[0]
          ? renewableResponse[0].values.map((row) => sqlToObject(["id", ...years], row))
          : []),
        ...(hydroResponse[0]
          ? hydroResponse[0].values.map((row) => sqlToObject(["id", ...years], row))
          : []),
      ];

      return sources.map((source) => {
        const sourcesGenerators = this.currentCase.input.generators.filter(
          (v) => v.source === source
        );
        const generatorsValues = sourcesGenerators.map((v) => {
          const match = generation.find((row) => row.id === v.id);
          if (!match) {
            return years.reduce((acc, year) => ({ ...acc, [year]: 0 }), {});
          }

          const { id, ...values } = match;
          return values;
        });
        const sumByYear = generatorsValues.reduce(
          (result, generatorVal) => {
            Object.entries(generatorVal).forEach(([year, value]) => {
              result[year] += valueModifier(value || 0);
            });
            return result;
          },
          years.reduce((acc, year) => ({ ...acc, [year]: 0 }), {})
        );
        let avgByYear;
        if (avg) {
          avgByYear = Object.entries(sumByYear).reduce(
            (acc, [year, val]) => ({ ...acc, [year]: val / generatorsValues.length }),
            {}
          );
        }
        return [
          `${source} (${unit})`,
          ...Object.entries(avg ? avgByYear : sumByYear)
            .sort((a, b) => a - b)
            .map(([, val]) => val),
        ];
      });
    },
    metricByNode(
      db,
      metric,
      unit,
      nodesArray,
      { /*  avg = false, */ valueModifier = (v) => v, includeStorage = false } = {}
    ) {
      // Generation by type
      const sqlRes = db.exec(
        `SELECT nodeID, year, SUM(value) as value FROM
          (SELECT nodeID, year, SUM(${metric}) as value FROM 'yearly_thermal_generators' group by nodeID, year
            UNION SELECT nodeID, year, SUM(${metric}) as value FROM 'yearly_hydro_generators' group by nodeID, year
            UNION SELECT nodeID, year, SUM(${metric}) as value FROM 'yearly_renewable_generators' group by nodeID, year
            ${
              includeStorage
                ? "UNION SELECT nodeID, year, SUM(discharge)-SUM(charge) as value FROM 'yearly_storages' group by nodeID, year"
                : ""
            }
            )
        GROUP BY nodeID, year`
      );
      const values = sqlRes[0]
        ? sqlRes[0].values.map((row) => sqlToObject(["nodeID", "year", "value"], row))
        : [];

      const years = [...new Set(nodesArray.map((v) => v.year))];
      const resultByNode = nodesArray.reduce(
        (acc, node) => ({
          ...acc,
          [node.id]: [
            `${node.name} (${unit})`,
            ...years.map((year) =>
              values.find((v) => v.year === year && v.nodeID === node.id)
                ? valueModifier(values.find((v) => v.year === year && v.nodeID === node.id).value)
                : 0
            ),
          ],
        }),
        {}
      );
      return Object.values(resultByNode);
    },
    annualReportFromDB(db) {
      const workbook = utils.book_new();
      const systemResponse = db.exec(`SELECT * FROM 'yearly_system'`);
      const nodesResponse = db.exec("SELECT * FROM yearly_nodes");
      const nodesKeys = nodesResponse[0].columns.map((colName) => colName);
      const nodesArray = nodesResponse[0].values.map((row) => sqlToObject(nodesKeys, row));

      // Generation by type
      const thermalResponse = db.exec(
        "SELECT year, SUM(generation) as generation FROM 'yearly_thermal_generators' group by year"
      );
      const renewableResponse = db.exec(
        "SELECT year, SUM(generation) as generation FROM 'yearly_renewable_generators' group by year"
      );
      const hydroResponse = db.exec(
        "SELECT year, SUM(generation) as generation FROM 'yearly_hydro_generators' group by year"
      );
      const storageResponse = db.exec(
        "SELECT year, SUM(discharge) as generation,  SUM(charge) as consumption FROM 'yearly_storages' group by year"
      );

      const storages = storageResponse[0]
        ? storageResponse[0].values.map((row) =>
            sqlToObject(["year", "generation", "consumption"], row)
          )
        : [];

      const generation = {
        thermal: thermalResponse[0]
          ? thermalResponse[0].values.map((row) => sqlToObject(["year", "generation"], row))
          : [],
        renewable: renewableResponse[0]
          ? renewableResponse[0].values.map((row) => sqlToObject(["year", "generation"], row))
          : [],
        hydro: hydroResponse[0]
          ? hydroResponse[0].values.map((row) => sqlToObject(["year", "generation"], row))
          : [],
      };

      if (systemResponse[0]) {
        const objectKeys = systemResponse[0].columns.map((colName) => colName);
        const jsonArray = systemResponse[0].values.map((row) => sqlToObject(objectKeys, row));
        const years = jsonArray.map((row) => row.year);
        if (jsonArray.length) {
          const yearlyFirmCapacity = years
            .map((year) => {
              return this.currentCase.input.generators.reduce((acc, generator) => {
                const { firm, capacity } = generator;
                const yearIndex = year - this.currentCase.settings.year_start;
                return acc + capacity[yearIndex] * (firm !== undefined ? firm / 100 : 1);
              }, 0);
            })
            .map((v) => Math.round((v + Number.EPSILON) * 100) / 100);
          const generationBySource = this.metricBySource(db, "generation", {
            unit: "GWh",
            valueModifier: (v) => v / 1000,
          });
          const worksheet = utils.aoa_to_sheet([
            ["Metric", ...years],
            ["Demand"],
            ["Peak (MW)", ...jsonArray.map((row) => row.peakLoad)],
            ["Energy (GWh)", ...jsonArray.map((row) => row.energyDemand)],
            ["Lost Energy (GWh)", ...jsonArray.map((row) => row.lostLoad)],
            ["Firm Capacity (MW)", ...yearlyFirmCapacity],
            [
              "Reserve Margin (%)",
              ...jsonArray
                .map((row) => row.peakLoad)
                .map((v, i) => ((yearlyFirmCapacity[i] - v) / yearlyFirmCapacity[i]) * 100)
                .map((v) => Math.round((v + Number.EPSILON) * 100) / 100),
            ],
            [],
            ["Peak Demand by Node"],
            ...[...new Set(nodesArray.map((v) => v.name))].map((node) => [
              `${node} (MW)`,
              ...nodesArray.filter((v) => v.name === node).map((v) => v.peakLoad),
            ]),
            [],
            ["Demand by Node"],
            ...[...new Set(nodesArray.map((v) => v.name))].map((node) => [
              `${node} (GWh)`,
              ...nodesArray.filter((v) => v.name === node).map((v) => v.energyDemand),
            ]),
            [],
            ["Capacity"],
            ...this.yearlyMetricByFuelTypeFromInput("capacity", years, "MW"),
            ...(storages.length
              ? [["STORAGE (MW)", ...this.yearlyStorageMetricFromInput("power_capacity", years)]]
              : []),
            [
              "Installed Capacity (MW)",
              ...this.yearlyMetricByFuelTypeFromInput("capacity", years)
                .map((row) => row.slice(1))
                .reduce(
                  (acc, array) => acc.map((sum, i) => sum + array[i]),
                  Array(years.length).fill(0)
                ),
            ],
            ...(storages.length
              ? [
                  [
                    "Installed Capacity w/Storage (MW)",
                    ...this.yearlyMetricByFuelTypeFromInput("capacity", years)
                      .map((row) => row.slice(1))
                      .reduce(
                        (acc, array) => acc.map((sum, i) => sum + array[i]),
                        this.yearlyStorageMetricFromInput("power_capacity", years)
                      ),
                  ],
                ]
              : []),
            [],
            ["Capacity by Node"],
            ...this.yearlyMetricByNodeFromInput("capacity", years),
            [],
            ["Capacity by Node w/ Storage"],
            ...this.yearlyMetricByNodeFromInput("capacity", years, {
              includeStorage: true,
              storageMetric: "power_capacity",
            }),
            [],
            ["Net: Imports/Exports"],
            [],
            ["Fuel Prices"],
            ...this.yearlyMetricByFuelTypeFromInput("fuel_price", years, {
              avg: true,
              onlyThermal: true,
              unit: "$/GJ",
            }),
            [],
            ["Generation by Fuel Type"],
            ["Renewable (GWh)", ...generation.renewable.map((row) => row.generation / 1000)],
            ["Thermal (GWh)", ...generation.thermal.map((row) => row.generation / 1000)],
            [
              "Total",
              ...[
                generation.thermal.map((row) => row.generation / 1000),
                generation.renewable.map((row) => row.generation / 1000),
              ].reduce(
                (acc, array) => acc.map((sum, i) => sum + array[i]),
                new Array(generation.thermal.length).fill(0)
              ),
            ],
            [],
            ["Generation by Source"],
            ...generationBySource,
            [
              "Total",
              ...generationBySource
                .map((row) => row.slice(1))
                .reduce(
                  (acc, array) => acc.map((sum, i) => sum + array[i]),
                  Array(years.length).fill(0)
                ),
            ],
            [],
            ...(storages.length
              ? [
                  ["Storage Generation (GWh)", ...storages.map((row) => row.generation / 1000)],
                  ["Storage Consumption (GWh)", ...storages.map((row) => row.consumption / 1000)],
                  [
                    "Total w/Storage",
                    ...[
                      storages.map((row) => row.generation / 1000),
                      storages.map((row) => -row.consumption / 1000),
                      generationBySource
                        .map((row) => row.slice(1))
                        .reduce(
                          (acc, array) => acc.map((sum, i) => sum + array[i]),
                          Array(years.length).fill(0)
                        ),
                    ].reduce(
                      (acc, array) => acc.map((sum, i) => sum + array[i]),
                      Array(years.length).fill(0)
                    ),
                  ],
                  [],
                ]
              : []),
            ["Generation by Node"],
            ...this.metricByNode(db, "generation", "GWh", nodesArray, {
              valueModifier: (v) => v / 1000,
            }),
            [],
            ["Generation by Node w/Storage"],
            ...this.metricByNode(db, "generation", "GWh", nodesArray, {
              valueModifier: (v) => v / 1000,
              includeStorage: true,
            }),
            [],
            ["Zonal Price"],
            ...[...new Set(nodesArray.map((v) => v.name))].map((node) => [
              `${node} ($/MWh)`,
              ...nodesArray.filter((v) => v.name === node).map((v) => v.averagePrice),
            ]),
            [
              "System Average Price ($/MWh)",
              ...[...new Set(nodesArray.map((v) => v.year))]
                .sort((a, b) => a - b)
                .map(
                  (year) =>
                    Math.round(
                      (nodesArray
                        .filter((v) => v.year === year)
                        .reduce((acc, v) => acc + v.averagePrice, 0) /
                        nodesArray.filter((v) => v.year === year).length +
                        Number.EPSILON) *
                        100
                    ) / 100
                ),
            ],
            [],
            ["SRMC by Source"],
            ...this.metricBySource(db, "avgSrmc", { avg: true, unit: "$/MWh" }),
            [],
            ["Emissions"],
            ...this.metricBySource(db, "emission", { unit: "Ton" }),
            ["Total", ...jsonArray.map((row) => row.totalEmissions)],
            [],
            ["Costs"],
            ["Total VOM Cost (MM$)", ...jsonArray.map((row) => row.totalVomCost / 1000000)],
            ["Total FOM Cost (MM$)", ...jsonArray.map((row) => row.totalFomCost / 1000000)],
            ["Total Fuel Cost (MM$)", ...jsonArray.map((row) => row.totalFuelCost / 1000000)],
            [
              "Total Emission Cost (MM$)",
              ...jsonArray.map((row) => row.totalEmissionCost / 1000000),
            ],
            [
              "Total Generation Cost (MM$)",
              ...jsonArray.map((row) => row.totalGenerationCost / 1000000),
            ],
            [],
            ["VOM Cost by Node"],
            ...[...new Set(nodesArray.map((v) => v.name))].map((node) => [
              `${node} (MM$)`,
              ...nodesArray.filter((v) => v.name === node).map((v) => v.totalVomCost / 1000000),
            ]),
            [],
            ["FOM Cost by Node"],
            ...[...new Set(nodesArray.map((v) => v.name))].map((node) => [
              `${node} (MM$)`,
              ...nodesArray.filter((v) => v.name === node).map((v) => v.totalFomCost / 1000000),
            ]),
            [],
            ["Fuel Cost by Node"],
            ...[...new Set(nodesArray.map((v) => v.name))].map((node) => [
              `${node} (MM$)`,
              ...nodesArray.filter((v) => v.name === node).map((v) => v.totalFuelCost / 1000000),
            ]),
            [],
            ["Emission Cost by Node"],
            ...[...new Set(nodesArray.map((v) => v.name))].map((node) => [
              `${node} (MM$)`,
              ...nodesArray
                .filter((v) => v.name === node)
                .map((v) => v.totalEmissionCost / 1000000),
            ]),
            [],
            ["Generation Cost by Node"],
            ...[...new Set(nodesArray.map((v) => v.name))].map((node) => [
              `${node} (MM$)`,
              ...nodesArray
                .filter((v) => v.name === node)
                .map((v) => v.totalGenerationCost / 1000000),
            ]),
          ]);
          utils.book_append_sheet(workbook, worksheet, "Annual Report");
        }
      }
      return { workbook, name: "System Annual Report.xlsx" };
    },

    resourceYearlyWorkbook(db, tableName) {
      const workbooks = [];
      const { timeline_settings } = this.currentCase.settings;
      const nYears = Math.ceil(
        Interval.fromDateTimes(
          DateTime.fromISO(timeline_settings.start_date),
          DateTime.fromISO(timeline_settings.end_date)
        ).length("years")
      );
      const years = Array(nYears)
        .fill(0)
        .map((_v, i) => DateTime.fromISO(timeline_settings.start_date).year + i);
      const columnsResponse = db.exec(`SELECT name FROM pragma_table_info('${tableName}')`);
      const properties = columnsResponse[0]
        ? columnsResponse[0].values
            .map((row) => row[0])
            .filter(
              (name) =>
                !["projectID", "name", "datetime", "nodeID", "branchID", "reserves"].includes(name)
            )
        : [];
      const namesRes = db.exec(`SELECT DISTINCT name from '${tableName}'`);
      const names = namesRes[0] ? namesRes[0].values : [];
      if (names.length) {
        years.forEach((year) => {
          properties.forEach((col) => {
            const workbook = utils.book_new();
            const namesSQL = names
              .map((name) => `SUM(CASE WHEN name = '${name}' THEN ${col} END) AS '${name}'`)
              .join();
            const startDate = DateTime.fromISO(`${year}-01-01T00:00:00.000-03:00`);
            const endDate = DateTime.fromISO(`${Number(year) + 1}-01-01T00:00:00.000-03:00`);
            const response = db.exec(`SELECT datetime,
                                          ${namesSQL}
                                          FROM '${tableName}'
                                          WHERE datetime > ${startDate.toUnixInteger()}
                                          AND datetime < '${endDate.toUnixInteger()}'
                                          GROUP BY datetime
                                          `);
            if (response[0]) {
              const objectKeys = response[0].columns.map((colName) => camelToTitleCase(colName));
              const jsonArray = response[0].values
                .map((row) => sqlToObject(objectKeys, row))
                .map((row) => ({
                  ...row,
                  Datetime: DateTime.fromISO(row.Datetime)
                    .setZone("utc")
                    .toSQL({ includeOffset: false }),
                }));
              if (jsonArray.length) {
                const opts = {
                  header: Object.keys(jsonArray[0]),
                };
                const worksheet = utils.json_to_sheet(jsonArray, opts);
                utils.book_append_sheet(workbook, worksheet, camelToTitleCase(col));
              }
              workbooks.push({
                workbook,
                name: `${snakeToTitleCase(tableName)}_${camelToTitleCase(col)}_${year}.csv`,
              });
            }
          });
        });
      }

      return workbooks;
    },
    yearlyResults(db) {
      const workbooks = [
        "branches",
        "demands",
        "hydro_generators",
        "renewable_generators",
        "storages",
        "thermal_generators",
        "nodes",
      ]
        .map((name) => {
          return this.resourceYearlyWorkbook(db, name);
        })
        .flat();
      return workbooks;
    },
    async npcSpreadsheetDownload(/* currentCase */) {
      this.npcDowloadProgress = {
        value: 1,
        text: "Initializing DuckDB...",
      };
      const output = this.outputs.find((v) => v.type_name === "PRODUCTION_COST");
      let annualSummaryWB = null;
      let annualReportWB = null;
      const yearlyResults = [];

      try {
        const npcDb = await initSqlDb(output);

        this.npcDowloadProgress.text = "Generating reports...";
        annualSummaryWB = await this.annualSummaryFromDB(npcDb);
        annualReportWB = await this.annualReportFromDB(npcDb);
        // yearlyResults = this.yearlyResults(npcDb);

        await npcDb.close();
      } catch (error) {
        console.error("Error downloading NPC spreadsheet:", error);
        this.npcDowloadProgress = { value: 0, text: "" };
        return;
      }

      this.npcDowloadProgress.text = "Creating ZIP file...";
      const zip = new Zip();
      [annualSummaryWB, annualReportWB, ...yearlyResults].forEach((wb) => {
        const wbout = write(wb.workbook, {
          bookSST: true,
          type: "binary",
        });
        zip.file(wb.name, wbout, { binary: true });
      });

      // writeFileXLSX(yearlyResults[3].workbook, yearlyResults[3].name);
      this.npcDowloadProgress = {
        value: 0,
        text: "",
      };
      zip.generateAsync({ type: "blob" }).then((content) => {
        // see FileSaver.js
        saveAs(content, `${this.currentCase.name}_NPC.zip`);
        // download(content, `${currentCase.name}_NPC.zip`);
      });
    },
  },
};
